import React, { useEffect, useState } from 'react';
import { Title, TextInput, PasswordInput, Toolbar, SaveButton, SelectInput, ArrayInput, useNotify, useTranslate, required, minLength, useAuthState, useAuthenticated, useGetIdentity, SimpleFormIterator } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import MoodIcon from '@mui/icons-material/Mood';

import SimpleForm from '../common/forms/SimpleForm';
import ColorInput from '../common/inputs/ColorInput';
import AchievementsGrid from './AchievementsGrid';
import ApiTokens from './ApiTokens';

import { getAccount, updateAccount } from '../../services/api';
import { changeTheme } from '../../actions/theme';


const inputSx = { minWidth: '250px' };

const AccountToolbar = () => (
    <Toolbar>
        <SaveButton />
    </Toolbar>
);

const TabPanel = ({ children, value, index, ...props }) => (
    <Box role="tabpanel" hidden={value !== index} {...props}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </Box>
);

const Account = () => {
    const [account, setAccount] = useState({});
    const [settings, setSettings] = useState({});
    const [tab, setTab] = useState(0);
    const theme = useSelector((state) => state.theme);
    const dispatch = useDispatch();
    const notify = useNotify();
    const translate = useTranslate();
    const { isLoading, authenticated } = useAuthState();
    const { refetch } = useGetIdentity();
    useAuthenticated();

    const actionChoices = [
        { id: 'timesheet_create', name: translate('Timesheet') },
        { id: 'timesheet_timer', name: translate('Timesheet timer') },
        { id: 'timesheet_stats', name: translate('Timesheet stats') },
        { id: 'timesheet_calendar', name: translate('Timesheet calendar') },
        { id: 'weekly_create', name: translate('Weekly') },
        { id: 'leaves_create', name: translate('Leaves') },
    ];

    useEffect(() => {
        const speedDialSettings = localStorage.getItem('speedDialSettings') ? JSON.parse(localStorage.getItem('speedDialSettings')) : null;
        if (speedDialSettings) {
            setSettings(speedDialSettings);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getAccount();
                if (response?.id) {
                    setAccount({
                        id: response.id,
                        firstname: response.firstname,
                        lastname: response.lastname,
                        color: response.color,
                        status: response.status
                    });
                }
            } catch (error) {
                console.error(error);
            }
        };

        if (!isLoading && authenticated) {
            fetchData();
        }
    }, [isLoading, authenticated]);

    const handleChangeTheme = () => {
        dispatch(changeTheme(theme === 'light' ? 'dark' : 'light'));
    };

    const handleSubmit = async (data) => {
        const response = await updateAccount(JSON.stringify(data));
        if (response.errors) {
            let message = '';
            for (const error in response.errors) {
                message += `${error}: ${response.errors[error]}\n`;
            }
            notify(message, "warning");
        }
        else {
            notify("Account updated");
            refetch();
        }
    };

    const handleChangeTab = (e, value) => {
        setTab(value);
    };

    const handleSubmitSettings = (data) => {
        localStorage.setItem('speedDialSettings', JSON.stringify(data));
        notify("Settings updated");
    };

    return (
        <Box my={3}>
            <Title title="Account" />
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tab} onChange={handleChangeTab}>
                        <Tab label={translate('Account')} />
                        <Tab label={translate('Achievements')} />
                        <Tab label={translate('Settings')} />
                        <Tab label={translate('API tokens')} />
                    </Tabs>
                </Box>
                <TabPanel value={tab} index={0}>
                    <SimpleForm record={account} onSubmit={handleSubmit} resource="account" toolbar={<AccountToolbar />}>
                        <TextInput source="status" label={<Box display="flex" alignItems="center" gap={0.5}><MoodIcon /> {translate('Status')}</Box>} sx={inputSx} />
                        <TextInput source="firstname" validate={required()} sx={inputSx} />
                        <TextInput source="lastname" validate={required()} sx={inputSx} />
                        <Box sx={{ ...inputSx, marginBottom: '20px' }}>
                            <ColorInput sx={inputSx} />
                        </Box>
                        <PasswordInput source="password" validate={minLength(8)} sx={inputSx} />
                    </SimpleForm>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                    <AchievementsGrid userId={account?.id} />
                </TabPanel>
                <TabPanel value={tab} index={2}>
                    <SimpleForm record={settings} resource="settings" toolbar={<AccountToolbar />} onSubmit={handleSubmitSettings}>
                        <Typography variant="h6" gutterBottom>Global</Typography>
                        <FormControl>
                            <FormGroup aria-label="position" row>
                                <FormControlLabel
                                    control={<Switch color="primary" onChange={handleChangeTheme} checked={theme === 'dark'} />}
                                    label="Dark theme"
                                    labelPlacement="start"
                                    sx={{ margin: 0 }}
                                />
                            </FormGroup>
                        </FormControl>
                        <Typography variant="h6" mt={2} gutterBottom>{translate('Quick actions')}</Typography>
                        <SelectInput source="show" label={translate('Visible on')} defaultValue="dashboard" choices={[{ id: 'dashboard', name: 'Dashboard' }, { id: 'pages', name: translate('On all pages') }, { id: 'disabled', name: translate('Disabled') }]} />
                        <ArrayInput source="actions" resource="settings" defaultValue={[{ action: 'timesheet_create' }]}>
                            <SimpleFormIterator >
                                <SelectInput label={false} source="action" choices={actionChoices} />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </SimpleForm>
                </TabPanel>
                <TabPanel value={tab} index={3}>
                    <ApiTokens />
                </TabPanel>
            </Card>
        </Box>
    );
};

export default Account;